import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ShoppingCart, Minus, Plus, Check, Truck, Shield, ChevronLeft, ChevronRight } from 'lucide-react';
import { getProductBySlug, getProducts } from '../services/api';
import { useCart } from '../context/CartContext';
import Header from '../components/Header';
import Footer from '../components/Footer';

const ProductDetail = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { addToCart } = useCart();

    const [product, setProduct] = useState(null);
    const [relatedProducts, setRelatedProducts] = useState([]);
    const [quantity, setQuantity] = useState(1);
    const [loading, setLoading] = useState(true);
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);
    const [showSuccess, setShowSuccess] = useState(false);

    useEffect(() => {
        loadProduct();
    }, [slug]);

    const loadProduct = async () => {
        setLoading(true);
        const res = await getProductBySlug(slug);
        if (res.success) {
            setProduct(res.data);
            setSelectedImageIndex(0);

            // Load related products
            const relatedRes = await getProducts({ category: res.data.categoryId });
            if (relatedRes.success) {
                setRelatedProducts(relatedRes.data.filter(p => p.id !== res.data.id).slice(0, 4));
            }
        } else {
            navigate('/shop');
        }
        setLoading(false);
    };

    const handleAddToCart = () => {
        addToCart(product, quantity);
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 2000);
    };

    const nextImage = () => {
        if (product?.images) {
            setSelectedImageIndex((prev) => (prev + 1) % product.images.length);
        }
    };

    const prevImage = () => {
        if (product?.images) {
            setSelectedImageIndex((prev) => (prev - 1 + product.images.length) % product.images.length);
        }
    };

    if (loading) {
        return (
            <>
                <Header />
                <div className="min-h-screen flex items-center justify-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                </div>
                <Footer />
            </>
        );
    }

    if (!product) return null;

    const productImages = product.images || [product.image];

    return (
        <>
            <Header />
            <div className="min-h-screen bg-gray-50 py-8">
                <div className="max-w-7xl mx-auto px-4">
                    {/* Breadcrumb */}
                    <div className="flex items-center gap-2 text-sm text-gray-600 mb-6">
                        <Link to="/" className="hover:text-primary">Home</Link>
                        <span>/</span>
                        <Link to="/shop" className="hover:text-primary">Shop</Link>
                        <span>/</span>
                        <span className="text-gray-900">{product.name}</span>
                    </div>

                    <div className="grid lg:grid-cols-2 gap-12 mb-16">
                        {/* Image Gallery */}
                        <div className="space-y-4">
                            {/* Main Image */}
                            <div className="relative bg-white rounded-lg overflow-hidden shadow-lg group">
                                <img
                                    src={productImages[selectedImageIndex]}
                                    alt={product.name}
                                    className="w-full aspect-square object-cover"
                                />

                                {/* Navigation Arrows */}
                                {productImages.length > 1 && (
                                    <>
                                        <button
                                            onClick={prevImage}
                                            className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                                        >
                                            <ChevronLeft className="w-6 h-6 text-gray-800" />
                                        </button>
                                        <button
                                            onClick={nextImage}
                                            className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                                        >
                                            <ChevronRight className="w-6 h-6 text-gray-800" />
                                        </button>
                                    </>
                                )}

                                {/* Image Counter */}
                                {productImages.length > 1 && (
                                    <div className="absolute bottom-4 right-4 bg-black/60 text-white px-3 py-1 rounded-full text-sm">
                                        {selectedImageIndex + 1} / {productImages.length}
                                    </div>
                                )}
                            </div>

                            {/* Thumbnail Gallery */}
                            {productImages.length > 1 && (
                                <div className="grid grid-cols-4 gap-3">
                                    {productImages.map((img, index) => (
                                        <button
                                            key={index}
                                            onClick={() => setSelectedImageIndex(index)}
                                            className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${selectedImageIndex === index
                                                    ? 'border-primary shadow-md scale-105'
                                                    : 'border-gray-200 hover:border-gray-300'
                                                }`}
                                        >
                                            <img
                                                src={img}
                                                alt={`${product.name} ${index + 1}`}
                                                className="w-full h-full object-cover"
                                            />
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Product Info */}
                        <div className="space-y-6">
                            <div>
                                <p className="text-sm text-primary font-medium mb-2">{product.category}</p>
                                <h1 className="text-4xl font-bold text-gray-900 mb-4">{product.name}</h1>
                                <p className="text-3xl font-bold text-primary">₹{product.price.toLocaleString('en-IN')}</p>
                            </div>

                            {/* Stock Status */}
                            <div className="flex items-center gap-2">
                                {product.stockQuantity > 0 ? (
                                    <>
                                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                                        <span className="text-green-700 font-medium">
                                            In Stock ({product.stockQuantity} available)
                                        </span>
                                    </>
                                ) : (
                                    <>
                                        <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                                        <span className="text-red-700 font-medium">Out of Stock</span>
                                    </>
                                )}
                            </div>

                            {/* Description */}
                            <p className="text-gray-700 leading-relaxed">{product.description}</p>

                            {/* Quantity Selector */}
                            {product.stockQuantity > 0 && (
                                <div className="space-y-4">
                                    <div className="flex items-center gap-4">
                                        <span className="font-medium text-gray-900">Quantity:</span>
                                        <div className="flex items-center border border-gray-300 rounded-lg">
                                            <button
                                                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                                className="p-3 hover:bg-gray-100 transition-colors"
                                            >
                                                <Minus className="w-4 h-4" />
                                            </button>
                                            <span className="px-6 py-2 font-medium">{quantity}</span>
                                            <button
                                                onClick={() => setQuantity(Math.min(product.stockQuantity, quantity + 1))}
                                                className="p-3 hover:bg-gray-100 transition-colors"
                                            >
                                                <Plus className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Add to Cart Button */}
                                    <button
                                        onClick={handleAddToCart}
                                        className="w-full bg-primary hover:bg-primary-strong text-white py-4 rounded-lg font-semibold text-lg transition-all duration-300 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl hover:scale-[1.02]"
                                    >
                                        {showSuccess ? (
                                            <>
                                                <Check className="w-6 h-6" />
                                                Added to Cart!
                                            </>
                                        ) : (
                                            <>
                                                <ShoppingCart className="w-6 h-6" />
                                                Add to Cart
                                            </>
                                        )}
                                    </button>
                                </div>
                            )}

                            {/* Trust Badges */}
                            <div className="grid grid-cols-2 gap-4 pt-6 border-t">
                                <div className="flex items-center gap-3">
                                    <Check className="w-5 h-5 text-green-600" />
                                    <span className="text-sm text-gray-700">Quality Assured</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <Shield className="w-5 h-5 text-blue-600" />
                                    <span className="text-sm text-gray-700">Secure Packaging</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <Truck className="w-5 h-5 text-orange-600" />
                                    <span className="text-sm text-gray-700">Fast Delivery</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <Check className="w-5 h-5 text-purple-600" />
                                    <span className="text-sm text-gray-700">Expert Support</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Features & Benefits */}
                    <div className="grid md:grid-cols-2 gap-8 mb-16">
                        {product.features && product.features.length > 0 && (
                            <div className="bg-white rounded-lg p-8 shadow-md">
                                <h2 className="text-2xl font-bold mb-6 text-gray-900">Key Features</h2>
                                <ul className="space-y-3">
                                    {product.features.map((feature, index) => (
                                        <li key={index} className="flex items-start gap-3">
                                            <Check className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                                            <span className="text-gray-700">{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {product.benefits && product.benefits.length > 0 && (
                            <div className="bg-white rounded-lg p-8 shadow-md">
                                <h2 className="text-2xl font-bold mb-6 text-gray-900">Benefits</h2>
                                <ul className="space-y-3">
                                    {product.benefits.map((benefit, index) => (
                                        <li key={index} className="flex items-start gap-3">
                                            <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                                            <span className="text-gray-700">{benefit}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>

                    {/* Related Products */}
                    {relatedProducts.length > 0 && (
                        <div>
                            <h2 className="text-3xl font-bold mb-8 text-gray-900">Related Products</h2>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                                {relatedProducts.map((relatedProduct) => (
                                    <Link
                                        key={relatedProduct.id}
                                        to={`/product/${relatedProduct.slug}`}
                                        className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:scale-105"
                                    >
                                        <img
                                            src={relatedProduct.image}
                                            alt={relatedProduct.name}
                                            className="w-full aspect-square object-cover"
                                        />
                                        <div className="p-4">
                                            <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">{relatedProduct.name}</h3>
                                            <p className="text-primary font-bold">₹{relatedProduct.price.toLocaleString('en-IN')}</p>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <Footer />
        </>
    );
};

export default ProductDetail;
